import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Select, InputNumber, Row, Col } from 'antd';
import { InfoTooltip } from '@common/components';

// Hook to force functional components to rerender
// Usefull when some functionality does not trigger a rerender
function useRefresh() {
  const [value, setValue] = useState(false);
  return () => setValue(!value);
}

export default function ExpirationTypeSelect({ form }) {
  const refresh = useRefresh();
  const isCompletion =
    form.getFieldValue('expirationType') === 'COMPLETION_DATE';

  const [expirationNumber, setExpirationNumber] = useState(() => {
    const days = form.getFieldValue('daysToExpiry') || 365;
    if (days % 365 === 0) {
      return days / 365;
    }

    if (days % 30 === 0) {
      return days / 365;
    }

    return days;
  });

  const [period, setPeriod] = useState(() => {
    const days = form.getFieldValue('daysToExpiry');
    if (days % 365 === 0 || !days) {
      return 'years';
    }

    if (days % 30 === 0) {
      return 'months';
    }

    return 'days';
  });

  const updateDaysToExpiry = (daysToExpiry) => {
    form.setFieldsValue({ daysToExpiry });
  };

  const onExpirationTypeChange = (value) => {
    const completion = value === 'COMPLETION_DATE';
    if (completion) {
      onPeriodChange(period, expirationNumber);
    } else {
      updateDaysToExpiry(null);
    }
    refresh();
  };

  const onPeriodChange = (period, expirationNumber) => {
    if (!period || !expirationNumber) return;

    let multiplier = 1;
    if (period === 'years') {
      multiplier = 365;
    } else if (period === 'months') {
      multiplier = 30;
    }

    updateDaysToExpiry(multiplier * expirationNumber);
  };

  return (
    <>
      <Form.Item
        label={
          <>
            Expiration
            <InfoTooltip
              size="1x"
              title="Specify whether this item is not expirable, has its own expiration (Expiration Date), or expires after a set period of time post completion."
              leftOffset={8}
            />
          </>
        }
        name="expirationType"
        rules={[{ required: true, message: 'Select an expiration type' }]}
      >
        <Select size="large" onChange={onExpirationTypeChange}>
          <Select.Option key="NON_EXPIRABLE" value="NON_EXPIRABLE">
            Non Expirable
          </Select.Option>
          <Select.Option key="EXPIRATION_DATE" value="EXPIRATION_DATE">
            Expiration Date
          </Select.Option>
          <Select.Option key="COMPLETION_DATE" value="COMPLETION_DATE">
            Completion Date
          </Select.Option>
        </Select>
      </Form.Item>
      {isCompletion && (
        <Row gutter={[8, 8]}>
          <Form.Item name="daysToExpiry" hidden>
            <InputNumber />
          </Form.Item>
          <Col span={6}>
            <InputNumber
              style={{ width: '100%' }}
              value={expirationNumber}
              onChange={(value) => {
                setExpirationNumber(value);
                onPeriodChange(period, value);
              }}
              min={1}
              size="large"
              required
            />
          </Col>
          <Col span={18}>
            <Select
              style={{ width: '100%' }}
              value={period}
              onChange={(value) => {
                setPeriod(value);
                onPeriodChange(value, expirationNumber);
              }}
              size="large"
              required
            >
              <Select.Option key="days" value="days">
                days after completion
              </Select.Option>
              <Select.Option key="months" value="months">
                months after completion
              </Select.Option>
              <Select.Option key="years" value="years">
                years after completion
              </Select.Option>
            </Select>
          </Col>
        </Row>
      )}
    </>
  );
}

ExpirationTypeSelect.propTypes = {
  form: PropTypes.object.isRequired,
};
